import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Save,
  Trash2,
  Package,
  MapPin,
  IndianRupee,
  Hash,
  Ruler,
  Truck,
  Languages,
  ShieldAlert,
  Tag,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatNPR, formatQty, formatSize } from "../lib/utils";
import { deriveFloor, markupFor } from "../lib/pricing";
import { encodeAmount } from "../lib/cipher";
import { suggestFamilies, sizeChipsFor, groupForName } from "../lib/families";
import { suggestBrands } from "../lib/brands";
import { queueIfOffline, getQueue } from "../lib/outbox";
import {
  fetchWithSnapshot,
  loadSnapshot,
  applyPendingWrites,
  KEYS,
} from "../lib/snapshot";
import { toast } from "sonner";
import romanizeToNepali from "../lib/romanization";

const API_BASE = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
const API = `${API_BASE}/api`;

// Text inputs, not numbers, so a half-typed "1." does not get swallowed and so
// the Nepali keyboard behaves. Parsed on submit.
const num = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));

export default function ProductForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getAuthHeader } = useAuth();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [units, setUnits] = useState([]);
  const [sizeUnits, setSizeUnits] = useState([]);
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);

  // Set once the shopkeeper types a tag price themselves, after which the
  // markup stops overwriting it. Without this, correcting the cost would throw
  // away a price they had deliberately chosen.
  const [mrpTouched, setMrpTouched] = useState(false);

  // The item picked from the list, when adding. Never set while editing: the
  // suggestions do not appear there, because picking one would silently move an
  // existing product to another category and location.
  const [family, setFamily] = useState(null);

  // Whether the shopkeeper has chosen a location themselves. The form opens on
  // the first location in the list, and without this flag that default looks
  // exactly like a deliberate choice -- so picking Pressure Cooker left the item
  // on the bottom shelf instead of moving it to the godown, and said nothing.
  const [locationTouched, setLocationTouched] = useState(false);

  // Open only while the brand field has focus, so the list does not sit over
  // the fields below it for the whole time the form is being filled in.
  const [brandOpen, setBrandOpen] = useState(false);

  // How many have been saved without leaving the form. Only counts up when
  // "Save & add another" is used.
  const [addedCount, setAddedCount] = useState(0);

  // A typical item is a name, a size and three numbers; the other dozen fields
  // exist for the unusual ones. They fold away so the everyday case reads as
  // the short form it really is -- collapsed when adding, open when editing,
  // since an edit is usually there to reach one of the unusual fields.
  const [showMore, setShowMore] = useState(isEdit);

  const [form, setForm] = useState({
    name_en: "",
    name_np: "",
    brand: "",
    category: "",
    location: "",
    size_value: "",
    size_unit: "",
    gauge: "",
    variant_group: "",
    unit: "pcs",
    purchase_unit: "",
    pack_size: "",
    cost_price: "",
    mrp: "",
    min_price: "",
    quantity: "",
    quantity_type: "exact",
    low_stock_threshold: "5",
    supplier_id: "",
    weight_g: null, // derived from purchases; shown, never typed
    sku: "", // issued by the server; shown, never typed
  });

  useEffect(() => {
    fetchData();
    if (isEdit) fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    // These populate the dropdowns. Without a fallback the form is unusable
    // offline: no categories means nothing to pick, so nothing can be added.
    const load = async (key, path, fallback = []) => {
      try {
        const { data } = await fetchWithSnapshot(key, async () => {
          const res = await axios.get(`${API}${path}`, getAuthHeader());
          return res.data;
        });
        return data;
      } catch (err) {
        console.error(`Failed to load ${path}:`, err);
        return fallback;
      }
    };

    const [suppliersData, categoriesData, locationsData, unitsData, sizeUnitsData, shopData, productsData] =
      await Promise.all([
        load(KEYS.suppliers, "/suppliers"),
        load(KEYS.categories, "/categories"),
        load(KEYS.locations, "/locations"),
        load(KEYS.units, "/units"),
        load(KEYS.sizeUnits, "/size-units"),
        load(KEYS.shop, "/shop", null),
        load(KEYS.products, "/products"),
      ]);

    setSuppliers(suppliersData);
    setCategories(categoriesData);
    setLocations(locationsData);
    setUnits(unitsData);
    setSizeUnits(sizeUnitsData);
    setShop(shopData);
    // Queued writes included, the same as the bulk screen does. A size added
    // offline a moment ago still has to lend its group and its chip, or the
    // next size of the same item starts a second group behind its back.
    setProducts(applyPendingWrites(productsData, getQueue()));

    if (categoriesData.length === 0 || locationsData.length === 0) {
      toast.error("Failed to load form data");
      return;
    }

    if (!isEdit) {
      // Whatever this shop stocks most of, not whatever sorts first.
      //
      // Categories arrive sorted by name, so the first was "Boxed Items" -- a
      // utensils shop opened every new product on boxed because B precedes S.
      // Anything added without picking from the list was filed wrong, took the
      // wrong default size unit, was offered cooker brands, and got an X-series
      // item code that is never reissued.
      const commonest = (field, rows, fallback) => {
        const counts = {};
        for (const p of productsData || []) {
          if (p[field]) counts[p[field]] = (counts[p[field]] || 0) + 1;
        }
        const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
        return rows.find((r) => r.id === best?.[0]) || fallback;
      };

      const category = commonest("category", categoriesData, categoriesData[0]);
      // Location has the same problem, for the same reason: "Bottom Shelf"
      // sorted first, so every new product started there.
      const location = commonest("location", locationsData, locationsData[0]);

      setForm((prev) => ({
        ...prev,
        category: category.id,
        location: location.id,
        // Brass items are sized by number, boxed goods by litre. Offering the
        // right one first saves a dropdown hunt on every single item.
        size_unit: category.default_size_unit || "",
      }));
    }
  };

  const fetchProduct = async () => {
    try {
      let p;
      try {
        const res = await axios.get(`${API}/products/${id}`, getAuthHeader());
        p = res.data;
      } catch (netErr) {
        // Fall back to the cached list, with pending writes applied -- a
        // product created offline exists nowhere else yet, so without this you
        // could add an item and then not be able to open it.
        const snap = loadSnapshot(KEYS.products);
        if (!snap) throw netErr;
        p = applyPendingWrites(snap.data, getQueue()).find((x) => x.id === id);
        if (!p) throw netErr;
      }
      // Every read is defended, because an offline-created product comes from
      // the outbox rather than the server and may be missing anything.
      const str = (v) => (v === null || v === undefined ? "" : String(v));
      setForm({
        name_en: p.name_en || "",
        name_np: p.name_np || "",
        brand: p.brand || "",
        category: p.category || "",
        location: p.location || "shelf_top",
        size_value: str(p.size_value),
        size_unit: p.size_unit || "",
        gauge: str(p.gauge),
        variant_group: p.variant_group || "",
        unit: p.unit || "pcs",
        purchase_unit: p.purchase_unit || "",
        pack_size: str(p.pack_size),
        cost_price: str(p.cost_price),
        mrp: str(p.mrp),
        min_price: str(p.min_price),
        quantity: str(p.quantity),
        quantity_type: p.quantity_type || "exact",
        low_stock_threshold: str(p.low_stock_threshold ?? 5),
        supplier_id: p.supplier_id || "",
        weight_g: p.weight_g ?? null,
        sku: p.sku || "",
      });
      setMrpTouched(true); // an existing price is a chosen one
    } catch (err) {
      toast.error("Product not found");
      navigate("/inventory");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Groups already in use, so this is a pick rather than a spelling test. A
  // mistyped group is the one way this field fails badly: the size simply does
  // not join its siblings, and nothing says why.
  const existingGroups = useMemo(() => {
    const seen = new Map();
    for (const p of products) {
      if (!p.variant_group || seen.has(p.variant_group)) continue;
      seen.set(p.variant_group, p.name_en);
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [products]);

  const sizeUnitsById = useMemo(
    () => Object.fromEntries(sizeUnits.map((u) => [u.id, u])),
    [sizeUnits],
  );

  const activeCategory = useMemo(
    () => categories.find((c) => c.id === form.category) || null,
    [categories, form.category],
  );
  const activeUnit = useMemo(
    () => units.find((u) => u.id === form.unit) || null,
    [units, form.unit],
  );
  const markup = markupFor(activeCategory, shop);
  const minMargin = Number(shop?.min_margin_percent) || 10;

  // Switching category re-suggests the size unit, but only while it is still
  // whatever the last category suggested -- never over a deliberate choice.
  const handleCategoryChange = (categoryId) => {
    const next = categories.find((c) => c.id === categoryId);
    const wasSuggested = !form.size_unit || form.size_unit === activeCategory?.default_size_unit;
    setForm((prev) => ({
      ...prev,
      category: categoryId,
      size_unit: wasSuggested ? next?.default_size_unit || "" : prev.size_unit,
    }));
  };

  const brandOptions = useMemo(
    () => suggestBrands(form.brand, form.category, products),
    [form.brand, form.category, products],
  );

  /**
   * Already on the shelf? Same name, same size, same gauge.
   *
   * Carried over from the bulk screen when the two were merged. It is the only
   * guard against entering the same thing twice, which is easy to do once the
   * form stays open between saves.
   */
  const duplicate = useMemo(() => {
    const name = form.name_en.trim().toLowerCase();
    if (!name) return null;
    const size = num(form.size_value);
    const gauge = num(form.gauge);
    return (
      products.find(
        (p) =>
          p.id !== id &&
          p.name_en?.trim().toLowerCase() === name &&
          (p.size_value ?? null) === size &&
          (p.gauge ?? null) === gauge,
      ) || null
    );
  }, [products, id, form.name_en, form.size_value, form.gauge]);

  // Only when adding. On an existing product the name is already settled, and
  // picking an item from the list would move it to another category without
  // saying so.
  const suggestions = useMemo(
    () => (isEdit || family ? [] : suggestFamilies(form.name_en)),
    [isEdit, family, form.name_en],
  );

  // Sizes to offer. The shop's own come from the variant group, which is why
  // these work when editing too -- changing 14 to 16 is a normal correction.
  const chips = useMemo(
    () => sizeChipsFor(family, products, form.variant_group),
    [family, products, form.variant_group],
  );

  /**
   * Fill in from the picked item.
   *
   * Follows the same rule as handleCategoryChange above: overwrite a field that
   * is empty or still carries the previous suggestion, never a deliberate
   * choice. Someone who set the location before picking the item meant it.
   */
  const pickFamily = (f) => {
    setFamily(f);
    setForm((prev) => {
      const untouched = (value, previousSuggestion) =>
        !value || value === previousSuggestion;
      return {
        ...prev,
        name_en: f.en,
        name_np: f.np,
        category: f.cat,
        location: f.loc && !locationTouched ? f.loc : prev.location,
        size_unit: f.sizeUnit || "",
        unit: f.unit && untouched(prev.unit, family?.unit || "pcs") ? f.unit : prev.unit,
        // Karai and thali are bought by the kilo; the field sits under "More
        // details", so it has to be right without anyone opening that.
        purchase_unit:
          f.purchaseUnit && untouched(prev.purchase_unit, family?.purchaseUnit || "")
            ? f.purchaseUnit
            : prev.purchase_unit,
        // Joins whatever group this item's other sizes already use, falling
        // back to a derived one. Same helper the bulk screen uses, so the two
        // screens cannot put two sizes of one item in different groups.
        variant_group: groupForName(f.en, f.cat, products) || prev.variant_group,
        size_value: "",
        gauge: "",
      };
    });
  };

  // Typing again means the pick no longer describes what is being entered, so
  // the list comes back.
  //
  // The Nepali name goes with it, but only when it came from the pick. Left
  // behind, it stayed on the new item: typing over "Pressure Cooker" still read
  // प्रेसर कुकर underneath. One a shopkeeper typed themselves is theirs to keep.
  const handleNameChange = (value) => {
    const fromFamily = Boolean(family);
    setFamily(null);
    setForm((prev) => ({
      ...prev,
      name_en: value,
      name_np: fromFamily ? "" : prev.name_np,
    }));
  };

  // Cost is what the dealer charged; the tag price follows from the markup until
  // the shopkeeper says otherwise. Saves typing it on every item.
  const handleCostChange = (value) => {
    const cost = num(value);
    setForm((prev) => ({
      ...prev,
      cost_price: value,
      mrp: !mrpTouched && cost > 0 ? String(Math.round(cost * markup)) : prev.mrp,
    }));
  };

  const cost = num(form.cost_price) || 0;
  const mrp = num(form.mrp) || 0;
  const floor = deriveFloor(cost, minMargin, form.min_price === "" ? null : num(form.min_price));
  const margin = cost > 0 && mrp > 0 ? (((mrp - cost) / cost) * 100).toFixed(0) : null;
  // The floor written in the shop's tag cipher, when one is set up.
  const floorCode = encodeAmount(floor, shop?.cipher_keyword);

  /**
   * Save, and either leave or stay put.
   *
   * `stay` is what replaced the separate bulk-entry screen: the form keeps the
   * things that do not change down a shelf -- category, location, brand, the
   * units -- and clears only what does. One screen for adding, so there is
   * nothing to choose between.
   */
  const handleSubmit = async (e, stay = false) => {
    e.preventDefault();

    if (!form.name_en.trim()) {
      toast.error("Product name is required");
      return;
    }
    if (!form.mrp || mrp <= 0) {
      toast.error("A tag price is required / मूल्य राख्नुहोस्");
      return;
    }
    if (form.min_price !== "" && num(form.min_price) > mrp) {
      toast.error("The lowest price cannot be above the tag price");
      return;
    }

    setSaving(true);

    const payload = {
      name_en: form.name_en.trim(),
      name_np: form.name_np.trim(),
      brand: form.brand.trim(),
      category: form.category,
      location: form.location,
      size_value: num(form.size_value),
      size_unit: form.size_value === "" ? null : form.size_unit || null,
      gauge: num(form.gauge),
      // Derived when left empty on an add -- the field is under "More details"
      // now, and the bulk screen this form absorbed always derived it. Without
      // this, two sizes of a typed-in item never fold into one row. On an edit
      // an emptied field stays empty: clearing it is how you ungroup.
      variant_group:
        form.variant_group.trim() ||
        (!isEdit
          ? groupForName(form.name_en.trim(), form.category, products)
          : null),
      unit: form.unit || "pcs",
      purchase_unit: form.purchase_unit || null,
      pack_size: num(form.pack_size),
      cost_price: num(form.cost_price) || 0,
      mrp,
      min_price: num(form.min_price),
      quantity: num(form.quantity) || 0,
      quantity_type: form.quantity_type,
      low_stock_threshold: num(form.low_stock_threshold) ?? 5,
      supplier_id: form.supplier_id || null,
    };

    // A new product needs its id decided here, not by the server: an offline
    // sale may reference it before either has been sent, and the id is also
    // what makes a replayed create a no-op rather than a duplicate.
    const method = isEdit ? "put" : "post";
    const path = isEdit ? `/products/${id}` : "/products";
    const body = isEdit ? payload : { id: crypto.randomUUID(), ...payload };

    /** Clear only what differs between two items on the same shelf. */
    const nextOne = (saved) => {
      // Added to the local list straight away, so the next item sees it as a
      // sibling: its group, its size chip and the duplicate check all depend
      // on it being there before the next row is typed.
      if (saved) setProducts((prev) => [...prev, saved]);
      setAddedCount((n) => n + 1);
      setFamily(null);
      setMrpTouched(false);
      setForm((prev) => ({
        ...prev,
        name_en: "",
        name_np: "",
        size_value: "",
        gauge: "",
        variant_group: "",
        quantity: "",
        cost_price: "",
        mrp: "",
        min_price: "",
        sku: "",
      }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    try {
      const res = await axios({ method, url: `${API}${path}`, data: body, ...getAuthHeader() });
      toast.success(
        isEdit ? "Product updated! / सामान अपडेट भयो!" : "Product added! / सामान थपियो!",
      );
      if (stay) nextOne(res.data);
      else navigate("/inventory");
    } catch (err) {
      if (queueIfOffline(err, { method, path, body, label: payload.name_en })) {
        // Queued counts as saved: it is on the shelf either way, and retyping
        // it when the connection returns would be the wrong outcome.
        if (stay) nextOne({ ...body, _pending: true });
        else navigate("/inventory");
        return;
      }
      toast.error(err.response?.data?.detail || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this product? / यो सामान मेटाउने?")) return;

    const path = `/products/${id}`;
    try {
      await axios.delete(`${API}${path}`, getAuthHeader());
      toast.success("Product deleted");
      navigate("/inventory");
    } catch (err) {
      if (queueIfOffline(err, { method: "delete", path, label: `Delete ${form.name_en}` })) {
        navigate("/inventory");
        return;
      }
      toast.error("Failed to delete");
    }
  };

  // Romanization - Convert romanized text to Nepali
  const handleConvertToNepali = () => {
    const romanized = form.name_en.trim();
    if (!romanized) {
      toast.error("Enter romanized text first (e.g., 'steel thali')");
      return;
    }

    const nepali = romanizeToNepali(romanized);
    if (nepali) {
      setForm((prev) => ({ ...prev, name_np: nepali }));
      toast.success(`Converted: ${nepali}`);
    } else {
      toast.error("Could not convert. Try typing manually.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F5] flex items-center justify-center">
        <div className="loader"></div>
      </div>
    );
  }

  const field =
    "w-full h-12 px-4 border border-gray-300 rounded-xl";
  const label = "text-sm text-gray-500 mb-1 block";
  const card = "bg-white rounded-xl p-4 space-y-3";
  const heading = "font-semibold flex items-center gap-2";

  return (
    <div className="min-h-screen bg-[#F9F9F5]" data-testid="product-form-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-lg font-semibold">
                {isEdit ? "Edit Product" : "Add Product"}
              </h1>
              <p className="text-xs text-gray-500 font-nepali">
                {isEdit ? "सामान सम्पादन" : "नयाँ सामान"}
              </p>
            </div>
          </div>

          {isEdit && (
            <button
              onClick={handleDelete}
              className="p-2 text-red-500 hover:bg-red-50 rounded-full"
              data-testid="delete-btn"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      </header>

      {/* Form */}
      <form onSubmit={handleSubmit} className={`p-4 space-y-4 ${isEdit ? "pb-24" : "pb-40"}`}>
        {/* Product Name */}
        <div className={card}>
          <h3 className={heading}>
            <Package className="w-5 h-5 text-[#8B0000]" />
            Product Name / सामानको नाम
          </h3>

          <div>
            <label className={label}>
              English Name * (or type romanized: steel thali)
            </label>
            <input
              type="text"
              value={form.name_en}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Thali (or: thali)"
              className={field}
              data-testid="name-en-input"
            />

            {/* Adding only. Picking one keeps the spelling identical to every
                other size of the same item, which is what lets the inventory
                list fold them into a single row. */}
            {suggestions.length > 0 && (
              <div className="mt-2 border border-gray-200 rounded-xl overflow-hidden">
                {suggestions.map((f) => (
                  <button
                    key={f.en}
                    type="button"
                    onClick={() => pickFamily(f)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center justify-between"
                    data-testid={`suggest-${f.en}`}
                  >
                    <span>
                      {f.en}
                      <span className="ml-2 text-gray-500 font-nepali">{f.np}</span>
                    </span>
                    <span className="text-xs text-gray-400">
                      {f.sizeUnit ? sizeUnitsById[f.sizeUnit]?.name_np : "—"}
                    </span>
                  </button>
                ))}
              </div>
            )}

            <p className="text-xs text-gray-400 mt-1">
              💡 Just the item name — the size goes in its own box below
            </p>
          </div>

          <div>
            <label className={`${label} font-nepali`}>नेपाली नाम</label>
            <input
              type="text"
              value={form.name_np}
              onChange={(e) => handleChange("name_np", e.target.value)}
              placeholder="थाली"
              className={`${field} font-nepali`}
              data-testid="name-np-input"
            />

            {/* Only while this is empty, which now means only for an item the
                list did not know -- picking one fills the Nepali name itself.
                Offered rather than applied on save because the converter is
                good on romanised Nepali ("thali" gives थाली) and poor on
                English ("Pressure Cooker" gives प्रेस्सुरे कुकर), so its output
                has to be seen before it is kept. */}
            {!form.name_np && form.name_en.trim() && (
              <button
                type="button"
                onClick={handleConvertToNepali}
                className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#8B0000] font-medium"
                data-testid="convert-btn"
              >
                <Languages className="w-4 h-4" />
                Write it in Nepali
              </button>
            )}
          </div>

        </div>

        {/* Category & Location */}
        <div className={card}>
          <h3 className={heading}>
            <MapPin className="w-5 h-5 text-[#8B0000]" />
            Category & Location / वर्ग र स्थान
          </h3>

          <div>
            <label className={label}>Category / वर्ग</label>
            <select
              value={form.category}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className={`${field} bg-white`}
              data-testid="category-select"
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name_en} / {cat.name_np}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={label}>Location / स्थान</label>
            <select
              value={form.location}
              onChange={(e) => {
                setLocationTouched(true);
                handleChange("location", e.target.value);
              }}
              className={`${field} bg-white`}
              data-testid="location-select"
            >
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name_en} / {loc.name_np}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Brand. Sits after the category on purpose: the list of brands
            offered depends on it, so being asked for a brand first meant being
            asked before the form knew what kind of thing this was. */}
        <div className={card}>
          <h3 className={heading}>
            <Tag className="w-5 h-5 text-[#8B0000]" />
            Brand / ब्रान्ड
          </h3>

          <div>
            <div className="relative">
              <input
                type="text"
                value={form.brand}
                onChange={(e) => handleChange("brand", e.target.value)}
                onFocus={() => setBrandOpen(true)}
                // Delayed so a tap on a suggestion lands before the list closes.
                onBlur={() => setTimeout(() => setBrandOpen(false), 150)}
                placeholder="Prestige, Baltra…"
                className={field}
                data-testid="brand-input"
              />

              {/* A list, not a dropdown: most steel and brass is unbranded in
                  Nepal, so a brand that is not on the list has to stay typeable. */}
              {brandOpen && brandOptions.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-56 overflow-y-auto">
                  {brandOptions.map((b) => (
                    <button
                      key={b}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        handleChange("brand", b);
                        setBrandOpen(false);
                      }}
                      className="w-full px-4 py-2.5 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                      data-testid={`brand-option-${b}`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Type anything — most steel and brass has no brand at all
            </p>
          </div>
        </div>

        {/* Size */}
        <div className={card}>
          <h3 className={heading}>
            <Ruler className="w-5 h-5 text-[#8B0000]" />
            Size / नाप
          </h3>

          {/* Standard sizes where a standard exists, plus whatever this shop
              already stocks. Numbered items seed none -- see families.js. A
              shortcut, never a restriction: the box below still takes anything. */}
          {chips.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {chips.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => handleChange("size_value", String(v))}
                  className={`px-3 h-10 rounded-xl font-medium transition-colors ${
                    num(form.size_value) === v
                      ? "bg-[#8B0000] text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                  data-testid={`size-chip-${v}`}
                >
                  {v}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Size</label>
              <input
                type="text"
                inputMode="decimal"
                value={form.size_value}
                onChange={(e) => handleChange("size_value", e.target.value)}
                placeholder="14"
                className={field}
                data-testid="size-value-input"
              />
            </div>
            <div>
              <label className={label}>Measured in</label>
              <select
                value={form.size_unit}
                onChange={(e) => handleChange("size_unit", e.target.value)}
                className={`${field} bg-white`}
                data-testid="size-unit-select"
              >
                <option value="">—</option>
                {sizeUnits.map((su) => (
                  <option key={su.id} value={su.id}>
                    {su.name_np} ({su.name_en})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-xs text-gray-400">
            कराई नं. १४ · कुकर २ लिटर · थाली ११ इन्च
          </p>

          {/* Right here when the picked item comes in gauges (thali); under
              "more details" for everything else, where it would only be noise. */}
          {family?.gauge && (
            <div>
              <label className={label}>
                Gauge / गेज — thickness, optional
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={form.gauge}
                onChange={(e) => handleChange("gauge", e.target.value)}
                placeholder="22"
                className={field}
                data-testid="gauge-input"
              />
              <p className="text-xs text-gray-400 mt-1">
                Lower is thicker: 20 गेज is heavier and dearer than 24 गेज
              </p>
            </div>
          )}

          {duplicate && (
            <div
              className="p-3 rounded-xl bg-amber-50 text-amber-800 text-sm"
              data-testid="duplicate-warning"
            >
              <div className="font-medium">
                Already on the shelf: {duplicate.name_en}{" "}
                <span className="font-nepali">
                  {formatSize(duplicate, sizeUnitsById)}
                </span>
              </div>
              <div className="text-xs mt-0.5">
                {formatQty(duplicate.quantity)} in stock at{" "}
                {formatNPR(duplicate.mrp || 0)}. Saving this makes a second row —
                open that one instead if it is the same thing.
              </div>
            </div>
          )}

          {/* Issued by the server, so it only exists once the product has been
              saved -- nothing to show, or explain, before then. */}
          {form.sku && (
            <div>
              <label className={label}>Item code / सामान कोड</label>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                <span
                  className="font-mono font-bold text-xl tracking-widest text-[#8B0000]"
                  data-testid="sku"
                >
                  {form.sku}
                </span>
                <span className="text-xs text-gray-500">
                  Write this on the tag. Type it into search to find the item.
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Pricing */}
        <div className={card}>
          <h3 className={heading}>
            <IndianRupee className="w-5 h-5 text-[#8B0000]" />
            Pricing / मूल्य
          </h3>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Cost / खरिद मूल्य</label>
              <input
                type="text"
                inputMode="decimal"
                value={form.cost_price}
                onChange={(e) => handleCostChange(e.target.value)}
                placeholder="0"
                className={field}
                data-testid="cost-price-input"
              />
              <p className="text-xs text-gray-400 mt-1">Per piece. Never shown on the tag.</p>
            </div>

            <div>
              <label className={label}>Tag price * / मूल्य</label>
              <input
                type="text"
                inputMode="decimal"
                value={form.mrp}
                onChange={(e) => {
                  setMrpTouched(true);
                  handleChange("mrp", e.target.value);
                }}
                placeholder="0"
                className={field}
                data-testid="mrp-input"
              />
              <p className="text-xs text-gray-400 mt-1">
                {mrpTouched ? "What staff quote" : `Auto: cost × ${markup}`}
              </p>
            </div>
          </div>

          {margin !== null && (
            <div
              className={`text-sm p-2 rounded-lg ${
                parseFloat(margin) >= 0
                  ? "bg-green-50 text-green-700"
                  : "bg-red-50 text-red-700"
              }`}
            >
              Markup / नाफा: {margin}%
            </div>
          )}

          {floor > 0 && (
            <div className="text-sm p-3 rounded-lg bg-amber-50 text-amber-800 flex items-start gap-2">
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-medium">
                  Do not sell below {formatNPR(floor)}
                </div>
                <div className="text-xs mt-0.5">
                  Cost plus {minMargin}%. Staff need the owner's PIN to go lower.
                </div>
                {/* Only once a code word is set in Settings -- otherwise this
                    tells the shopkeeper to write down something that does not
                    exist yet. */}
                {floorCode && (
                  <div className="mt-2 pt-2 border-t border-amber-200">
                    <span className="text-xs">Write on the tag: </span>
                    <span
                      className="font-mono font-bold text-base tracking-widest"
                      data-testid="floor-code"
                    >
                      {floorCode}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Stock */}
        <div className={card}>
          <h3 className={heading}>
            <Hash className="w-5 h-5 text-[#8B0000]" />
            Stock / स्टक
          </h3>

          <div>
            <label className={label}>
              Current Stock{activeUnit ? ` (${activeUnit.name_np})` : ""}
            </label>
            <input
              type="text"
              inputMode={activeUnit?.allow_decimal ? "decimal" : "numeric"}
              value={form.quantity}
              onChange={(e) => handleChange("quantity", e.target.value)}
              placeholder="0"
              className={field}
              data-testid="quantity-input"
            />
          </div>
        </div>

        {/* Everything an ordinary item never needs, one tap away. */}
        <button
          type="button"
          onClick={() => setShowMore((v) => !v)}
          className="w-full py-3 rounded-xl border border-dashed border-gray-300 bg-white
                     text-gray-600 text-sm font-medium flex items-center justify-center gap-2"
          data-testid="more-details-toggle"
        >
          {showMore ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
          {showMore
            ? "Fewer details / कम विवरण"
            : "More details / थप विवरण — unit, supplier, gauge…"}
        </button>

        {showMore && (
          <>
            <div className={card}>
              <h3 className={heading}>
                <Ruler className="w-5 h-5 text-[#8B0000]" />
                Other details / अन्य विवरण
              </h3>

              {/* The gauge again, for items not picked from the list -- a pick
                  that comes in gauges shows it up in the Size section instead. */}
              {!family?.gauge && (
                <div>
                  <label className={label}>
                    Gauge / गेज — thickness, optional
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.gauge}
                    onChange={(e) => handleChange("gauge", e.target.value)}
                    placeholder="22"
                    className={field}
                    data-testid="gauge-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Lower is thicker: 20 गेज is heavier and dearer than 24 गेज
                  </p>
                </div>
              )}

            <div>
              <label className={label}>
                Show with its other sizes / अरू नापसँग — optional
              </label>
              <input
                type="text"
                list="variant-groups"
                value={form.variant_group}
                onChange={(e) => handleChange("variant_group", e.target.value)}
                placeholder="pick an item, or leave empty"
                className={field}
                data-testid="variant-group-input"
              />
              <datalist id="variant-groups">
                {existingGroups.map(([group, name]) => (
                  <option key={group} value={group}>
                    {name}
                  </option>
                ))}
              </datalist>
              <p className="text-xs text-gray-400 mt-1">
                Sizes sharing this collapse into one row in the list — "Karai,
                4 sizes" instead of four separate Karai. Pick the same one its
                other sizes use; leave it empty for a one-off.
              </p>
            </div>

            <div>
              <label className={label}>
                Lowest price / सबैभन्दा कम मूल्य — optional
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={form.min_price}
                onChange={(e) => handleChange("min_price", e.target.value)}
                placeholder="leave empty"
                className={field}
                data-testid="min-price-input"
              />
              <p className="text-xs text-gray-400 mt-1">
                Overrides the figure above. Best left empty — a fixed floor goes
                stale when the dealer's price changes.
              </p>
            </div>

              <div>
                <label className={label}>Low Stock Alert / कम स्टक</label>
                <input
                  type="text"
                  inputMode={activeUnit?.allow_decimal ? "decimal" : "numeric"}
                  value={form.low_stock_threshold}
                  onChange={(e) => handleChange("low_stock_threshold", e.target.value)}
                  placeholder="5"
                  className={field}
                  data-testid="threshold-input"
                />
              </div>

              <div>
                <label className={`${label} mb-2`}>Count Type / गणना प्रकार</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleChange("quantity_type", "exact")}
                    className={`flex-1 py-3 rounded-xl font-medium transition-colors ${
                      form.quantity_type === "exact"
                        ? "bg-[#8B0000] text-white"
                        : "bg-gray-100 text-gray-600"
                    }`}
                    data-testid="qty-type-exact"
                  >
                    Exact / सही
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChange("quantity_type", "approximate")}
                    className={`flex-1 py-3 rounded-xl font-medium transition-colors ${
                      form.quantity_type === "approximate"
                        ? "bg-[#8B0000] text-white"
                        : "bg-gray-100 text-gray-600"
                    }`}
                    data-testid="qty-type-approx"
                  >
                    ~Approx / अनुमानित
                  </button>
                </div>
              </div>
            </div>

          {/* Units */}
          <div className={card}>
            <h3 className={heading}>
              <Hash className="w-5 h-5 text-[#8B0000]" />
              How it is sold and bought / एकाइ
            </h3>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>Sold by / बिक्री एकाइ</label>
                <select
                  value={form.unit}
                  onChange={(e) => handleChange("unit", e.target.value)}
                  className={`${field} bg-white`}
                  data-testid="unit-select"
                >
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name_np} ({u.name_en})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={label}>Bought by / खरिद एकाइ</label>
                <select
                  value={form.purchase_unit}
                  onChange={(e) => handleChange("purchase_unit", e.target.value)}
                  className={`${field} bg-white`}
                  data-testid="purchase-unit-select"
                >
                  <option value="">same as sold</option>
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name_np} ({u.name_en})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  के.जी. for karai and thali
                </p>
              </div>
            </div>

            <div>
              <label className={label}>Pieces in one pack / प्याकमा थान</label>
              <input
                type="text"
                inputMode="decimal"
                value={form.pack_size}
                onChange={(e) => handleChange("pack_size", e.target.value)}
                placeholder="12"
                className={field}
                data-testid="pack-size-input"
              />
            </div>

            {form.weight_g ? (
              <div className="text-sm p-3 rounded-lg bg-gray-50 text-gray-600">
                One piece weighs about{" "}
                <span className="font-medium">{formatQty(form.weight_g)} g</span> —
                worked out from what you bought, not typed. It is what turns a
                per-kilo dealer price into a per-piece cost.
              </div>
            ) : null}
          </div>

          {/* Supplier */}
          <div className={card}>
            <h3 className={heading}>
              <Truck className="w-5 h-5 text-[#8B0000]" />
              Supplier / आपूर्तिकर्ता
            </h3>

            <select
              value={form.supplier_id}
              onChange={(e) => handleChange("supplier_id", e.target.value)}
              className={`${field} bg-white`}
              data-testid="supplier-select"
            >
              <option value="">No Supplier / आपूर्तिकर्ता छैन</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          </>
        )}

        {/* Submit */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 space-y-2">
          {/* Adding several in a row: this is what the separate bulk screen
              used to be for. Keeps the shelf's category, location, brand and
              units so the next item is only a name and a few numbers. */}
          {!isEdit && (
            <button
              type="button"
              onClick={(e) => handleSubmit(e, true)}
              disabled={saving}
              className="w-full h-12 border-2 border-[#8B0000] text-[#8B0000] font-semibold rounded-xl
                         active:scale-95 transition-all disabled:opacity-50
                         flex items-center justify-center gap-2"
              data-testid="save-and-next-btn"
            >
              <Package className="w-5 h-5" />
              Save &amp; add another
              {addedCount > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs">
                  {addedCount} added
                </span>
              )}
            </button>
          )}

          <button
            type="submit"
            disabled={saving}
            className="w-full h-14 bg-[#8B0000] text-white text-lg font-semibold rounded-xl
                       hover:bg-[#6B0000] active:scale-95 transition-all disabled:opacity-50
                       flex items-center justify-center gap-2"
            data-testid="save-btn"
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <Save className="w-5 h-5" />
                {isEdit
                  ? "Update Product / अपडेट गर्नु"
                  : "Add Product / थप्नुहोस्"}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
